import { defineComponent } from "../lib/component.ts";
import { enableDragScroll } from "../lib/drag-scroll.ts";
import { createScrollbar } from "../lib/faux-scrollbar.ts";
import { observeScroll } from "../lib/scroll-observer.ts";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function setsEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

interface WorldGalleryRefs {
  sticky?: HTMLElement;

  track?: HTMLElement;

  scrollbar?: HTMLElement;

  thumb?: HTMLElement;

  items?: HTMLElement[];
}

type Scrollbar = ReturnType<typeof createScrollbar>;

class WorldGallery extends defineComponent<WorldGalleryRefs>()(["track", "scrollbar", "thumb"]) {
  #scrollbar: Scrollbar | null = null;

  #resizeObserver: ResizeObserver | null = null;

  #cols = 0;
  #maxShift = 0;
  #verticalRange = 0;

  #visibleIndices: Set<number> = new Set();

  connectedCallback(): void {
    super.connectedCallback();

    const { track, scrollbar, thumb, items } = this.refs;
    if (!Array.isArray(items) || items.length === 0) return;

    const isDesignMode = window.Shopify?.designMode === true;
    const prefersReducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    const isHoverDevice = window.matchMedia("(hover: hover)").matches;

    if (isDesignMode || prefersReducedMotion) {
      this.#initFallback(track, scrollbar, thumb);
      return;
    }

    if (!isHoverDevice && items.length > 3) {
      this.#initTwoRowMobile();
      return;
    }

    if (!isHoverDevice) {
      this.#initFallback(track, scrollbar, thumb);
      return;
    }

    this.#scrollbar = createScrollbar(track, scrollbar, thumb, this.signal);
    this.#initPinning();
  }

  disconnectedCallback(): void {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    super.disconnectedCallback();
  }

  #initFallback(track: HTMLElement, scrollbar: HTMLElement, thumb: HTMLElement): void {
    this.classList.add("world-gallery--fallback");
    this.#scrollbar = createScrollbar(track, scrollbar, thumb, this.signal);
    if (window.matchMedia("(hover: hover)").matches) {
      enableDragScroll(track, this.signal, { itemSelector: ":scope > *" });
    }
    track.addEventListener("scroll", () => this.#updateVideos(), {
      passive: true,
      signal: this.signal,
    });
    observeScroll(this, () => this.#updateVideos(), this.signal, {
      onExit: () => this.#pauseAllVideos(),
    });
  }

  #initPinning(): void {
    const { track } = this.refs;
    this.#updateExtraScroll();

    this.#resizeObserver = new ResizeObserver(() => this.#updateExtraScroll());
    this.#resizeObserver.observe(track);

    observeScroll(this, () => this.#onScroll(), this.signal, {
      onExit: () => this.#pauseAllVideos(),
    });

    this.signal.addEventListener(
      "abort",
      () => {
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = null;
      },
      { once: true },
    );
  }

  #initTwoRowMobile(): void {
    const { sticky, track, items } = this.refs;
    if (!sticky || !Array.isArray(items)) return;

    this.#cols = Math.ceil(items.length / 2);

    this.classList.add("world-gallery--two-row");
    track.style.setProperty("--cols", String(this.#cols));

    for (let i = 0; i < items.length; i++) {
      items[i].dataset.row = i < this.#cols ? "top" : "bottom";
    }

    this.#updateTwoRowLayout();
    this.#resizeObserver = new ResizeObserver(() => this.#updateTwoRowLayout());
    this.#resizeObserver.observe(items[0]);

    observeScroll(this, () => this.#onMobileScroll(), this.signal, {
      onExit: () => this.#pauseAllVideos(),
    });

    this.signal.addEventListener(
      "abort",
      () => {
        this.#resizeObserver?.disconnect();
        this.#resizeObserver = null;
      },
      { once: true },
    );
  }

  #updateTwoRowLayout(): void {
    const { items } = this.refs;
    if (!Array.isArray(items) || items.length === 0) return;

    const itemWidth = items[0].offsetWidth;
    this.#maxShift = (this.#cols - 1) * itemWidth;

    const stickyHeight = itemWidth * 2;
    this.#verticalRange = itemWidth / 2;
    this.style.height = `${stickyHeight + this.#maxShift * 2}px`;
  }

  #onMobileScroll(): void {
    const { track, items } = this.refs;
    if (!Array.isArray(items) || this.#maxShift === 0) return;

    const rect = this.getBoundingClientRect();
    const scrollRange = rect.height - window.innerHeight;
    if (scrollRange <= 0) return;
    const progress = Math.max(0, Math.min(1, -rect.top / scrollRange));

    track.style.transform = `translateY(${-progress * this.#verticalRange}px)`;

    const topOffset = -progress * this.#maxShift;
    const bottomOffset = -(1 - progress) * this.#maxShift;

    for (let i = 0; i < items.length; i++) {
      const offset = i < this.#cols ? topOffset : bottomOffset;
      items[i].style.transform = `translateX(${offset}px)`;
    }

    this.#updateVideosTwoRow(topOffset, bottomOffset);
  }

  #updateVideosTwoRow(topOffset: number, bottomOffset: number): void {
    const { items } = this.refs;
    if (!Array.isArray(items)) return;

    const itemWidth = this.#maxShift / (this.#cols - 1);
    const viewportWidth = window.innerWidth;

    const visible: Set<number> = new Set();

    for (let i = 0; i < items.length; i++) {
      const indexInRow = i < this.#cols ? i : i - this.#cols;
      const rowOffset = i < this.#cols ? topOffset : bottomOffset;
      const visualLeft = indexInRow * itemWidth + rowOffset;

      if (visualLeft < viewportWidth && visualLeft + itemWidth > 0) {
        visible.add(i);
      }
    }

    if (setsEqual(visible, this.#visibleIndices)) return;

    for (let i = 0; i < items.length; i++) {
      const video = items[i].querySelector("video");
      if (!video) continue;

      if (visible.has(i)) {
        video.play();
      } else {
        video.pause();
      }
    }

    this.#visibleIndices = visible;
  }

  #updateExtraScroll(): void {
    const { track } = this.refs;

    const extraScroll = track.scrollWidth - track.clientWidth;
    this.style.setProperty("--extra-scroll", `${Math.max(0, extraScroll)}px`);
    this.#scrollbar?.update();
    this.#onScroll();
  }

  #onScroll(): void {
    const { track } = this.refs;

    const extraScroll = track.scrollWidth - track.clientWidth;
    if (extraScroll <= 0) return;

    const progress = Math.max(0, Math.min(1, -this.getBoundingClientRect().top / extraScroll));
    track.scrollLeft = progress * extraScroll;
    this.#scrollbar?.update();
    this.#updateVideos();
  }

  #updateVideos(): void {
    const { track, items } = this.refs;
    if (!Array.isArray(items)) return;

    const trackWidth = track.clientWidth;
    const scrollLeft = track.scrollLeft;
    const visible: Set<number> = new Set();

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const left = item.offsetLeft - scrollLeft;
      const right = left + item.offsetWidth;

      if (left < trackWidth && right > 0) {
        visible.add(i);
      } else if (visible.size > 0) {
        break;
      }
    }

    if (setsEqual(visible, this.#visibleIndices)) return;

    for (let i = 0; i < items.length; i++) {
      const video = items[i].querySelector("video");
      if (!video) continue;

      if (visible.has(i)) {
        video.play();
      } else {
        video.pause();
      }
    }

    this.#visibleIndices = visible;
  }

  #pauseAllVideos(): void {
    const { items } = this.refs;
    if (!Array.isArray(items)) return;

    for (const item of items) {
      const video = item.querySelector("video");
      if (video) video.pause();
    }

    this.#visibleIndices = new Set();
  }
}

if (!customElements.get("world-gallery")) {
  customElements.define("world-gallery", WorldGallery);
}
